// Session-scoped signal for "the authenticated Mixpanel identity has been
// resolved." The app-shell loader identifies the user and calls
// `markIdentityReady`; `usePageTracking` defers the first `page_viewed` on an
// authenticated route until this flips.
//
// Why this exists: on a cold load (hard refresh / bookmarked deep link) the
// app-shell route renders its `hydrateFallbackElement` while its async loader
// runs. During that partial-hydration window React Router commits the parent
// layout — which mounts `usePageTracking` — so the first `page_viewed` would
// otherwise fire before the loader identifies the user, attributing it to the
// anonymous `$device` id. Identifying inside the loader alone does not win
// that race; gating the event on this signal does.

let ready = false;
const waiters = new Set<() => void>();

// Called by the app-shell loader once the authenticated user has been
// identified in Mixpanel. Idempotent — the loader runs on every authenticated
// navigation, but only the first call notifies pending waiters.
export function markIdentityReady(): void {
  if (ready) return;
  ready = true;
  const pending = [...waiters];
  waiters.clear();
  for (const notify of pending) notify();
}

export function isIdentityReady(): boolean {
  return ready;
}

// Runs `notify` once identity is ready — synchronously if it already is.
// Returns an unsubscribe so a caller can drop its waiter if the route changes
// (or it unmounts) before identity resolves.
export function onIdentityReady(notify: () => void): () => void {
  if (ready) {
    notify();
    return () => {};
  }
  waiters.add(notify);
  return () => {
    waiters.delete(notify);
  };
}

// Clears identity state on logout so a subsequent login in the same tab
// re-gates the first authenticated `page_viewed` cleanly.
export function resetIdentityReady(): void {
  ready = false;
  waiters.clear();
}
